import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/authStore";
import { acceptDisclaimer, login } from "../service/auth_service";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import useUserContextStore from "../../../store/userContextStore";

const DISCLAIMER_CONTENT = [
  { type: "h4", text: "DISCLAIMER TITLE" },
  { type: "p", text: "Terms and Conditions" },
  {
    type: "p",
    text: `Please read this user agreement (the “User Agreement”) carefully before using this website (together with all content contained thereon or any pages linked thereto, the “Website”). By accessing or using the Website you are agreeing that you have read, understand and accept the terms and conditions of this User Agreement, will abide by this User Agreement, agree to the limitations herein and are legally bound by this User Agreement without limitation or qualification, which you will confirm by clicking “Agree” at the end of this User Agreement. If you do not agree with any provisions of the User Agreement, do not use the Website or save, reference or download anything from it. Beatrice Advisors LP or any of its affiliates (collectively, “Beatrice Advisors”) reserves the right to change, modify, add or remove portions of this User Agreement or any content posted on the Website at any time without notice and without liability. By using the Website after we have posted changes, you are agreeing to be bound by the User Agreement as amended. We suggest that you review this User Agreement periodically for changes. At any time and for any reason Beatrice Advisors may terminate or suspend your right to use all or any portion of the Website.`,
  },
  { type: "p", text: "Copyright and Restrictions on Use" },
  {
    type: "p",
    text: `The information available on or via the Website (the “Website Information”), including all design, text and pictures, is owned or licensed by Beatrice Advisors. The Website Information is protected by U.S. and international intellectual property laws. The trademarks, service marks and logos used and displayed on the Website are exclusive trademarks and service marks of Beatrice Advisors. No portion of the Website Information may be copied, modified, reproduced, transmitted, displayed, performed, stored, distributed, abridged, linked to or otherwise used in any way without Beatrice Advisors’ prior written consent; provided, however, you may download, where specifically permitted, one copy of the documents posted on the Website on any single computer solely for your personal, non-commercial use, provided you keep intact and properly display all copyright and other proprietary notices. The Website Information may be discussed confidentially with your financial, legal or tax advisors.`,
  },
  {
    type: "p",
    text: `You agree not to remove any notice signifying the intellectual property or other proprietary rights of Beatrice Advisors or any of its third party vendors or third-party managers (together with Beatrice Advisors, “we”, “us” or “our”). Except as expressly provided in this User Agreement, we do not grant any express or implied right to you under any copyright, trademark, or other intellectual property or proprietary right. All rights not expressly granted are reserved.`,
  },
  {
    type: "p",
    text: `In accessing any part of this Website, you agree, represent and warrant that you (a) have full authority and all rights necessary to enter into and fully perform all of your obligations pursuant to this User Agreement; (b) have the skills and equipment necessary to access and use this Website; (c) shall not access or attempt to access any part of this Website that is addressed to any person, other than yourself, who has obtained a password from Beatrice Advisors; (d) shall not disseminate, share or transmit the information contained herein or permit any other person to access this Website who is not so authorized by Beatrice Advisors; (e) shall not use this Website in such a way that disrupts, interferes with or restricts the use of this Website by other users; (f) shall not upload, display or transmit any materials through this Website which are false, offensive, defamatory, threatening, obscene, unlawful or which infringe the rights of any other person anywhere in the world; (g) shall not reverse engineer, decompile, copy or adapt any software or other code or scripts forming part of this Website or attempt to transmit to or via this website any information that contains a virus, worm, trojan horse or other harmful or disruptive component; and (h) shall not change, modify, delete, interfere with or misuse data contained on this Website and entered by or relating to any third party user of this Website.`,
  },
  {
    type: "p",
    text: `This Website is designed to be accessed through its principal home pages and such other pages as Beatrice Advisors may decide. If you access this Website through individual pages directly, which are not designed for this purpose, you may not see important information that is relevant to the content of this Website.`,
  },
  { type: "p", text: "Disclaimers" },
  {
    type: "p",
    text: `Nothing contained on the Website constitutes investment, legal, accounting or tax advice. Neither the Website Information, nor any opinion contained in the Website constitutes a solicitation or offer by Beatrice Advisors, any fund or other entity managed by them or any third-party manager, to buy or sell any securities or financial instruments. You are solely responsible for decisions based on Website Information, and as consideration for access to the Website, you agree to hold Beatrice Advisors and its affiliates harmless from and against any claims of any nature whatsoever for damages arising from any decisions that you make based on such information. Beatrice Advisors makes no representations that services discussed on this Website are available or appropriate for sale or use in all jurisdictions or by all investors. Those who access this Website do so at their own initiative and are responsible for compliance with any laws or regulations applicable to them. All Website Information and the Website itself are provided “as is” and “as available” without any warranty of any kind. We make no representations or warranties. To the fullest extent permitted by law, we hereby expressly disclaim any and all express or implied representations and warranties of any kind to you and/or any third party including, without limitation, any warranty as to merchantability or fitness for a particular purpose, non-infringement, the use or inability to use the Website, or the correctness, quality, accuracy, reliability, performance, completeness, timeliness or continued availability of the Website. Without limiting the foregoing, we make no representation or warranty that the Website, including the Website Information, will conform to any description thereof or perform any desired operations or functions, be uninterrupted or error-free, or be free of viruses, worms, trojan horses or other disabling or harmful components.`,
  },
  {
    type: "p",
    text: `You acknowledge that if you rely on any Website Information, you do so at your sole and exclusive risk. We take reasonable efforts to ensure that the Website is secure but we do not guarantee the security of the Website.`,
  },
  { type: "p", text: "Website Information" },
  {
    type: "p",
    text: `THE PAST PERFORMANCE OF ANY INVESTMENT, INVESTMENT STRATEGY OR INVESTMENT STYLE IS NOT INDICATIVE OF FUTURE PERFORMANCE. NO REPRESENTATION IS BEING MADE THAT ANY INVESTMENT WILL OR IS LIKELY TO ACHIEVE PROFITS OR LOSSES SIMILAR TO THOSE ACHIEVED IN THE PAST, OR THAT SIGNIFICANT LOSSES WILL BE AVOIDED.`,
  },
  {
    type: "p",
    text: `The performance results contained herein are provided net of fees and expenses payable by clients to the managers of such funds and accounts, and include reinvestment of dividends, interest and other earnings. Affiliates of the managers of the respective funds and accounts may manage other funds and accounts, the results of which may not be shown here.`,
  },
  {
    type: "p",
    text: `The returns of several market indices are provided for comparison purposes only and such comparison does not mean that there will necessarily be a correlation between the investment returns and these indices. Such indices are unmanaged and are not subject to fees and expenses, including transaction costs typically associated with funds and managed accounts. Investments cannot be made directly in indices and such indices may re-invest dividends and income.`,
  },
  {
    type: "p",
    text: `The Website, including the Website Information, is produced for informational purposes only and shall not serve as the basis for any of your investment decisions. Any investment returns or estimates, forward-looking statements, forecasts or indications of past performance are for informational purposes only, may not be current and may not be indicative of future performance. Such information does not constitute an offer, solicitation or recommendation with respect to the purchase or sale of any securities, investments or other property. Any transactions referenced on this Website are included as representative transactions and are not necessarily reflective of overall performance. All materials accessible through the Website must be considered in their entirety, including any footnotes, legal disclaimers, restrictions, disclosures, and copyright or proprietary notices. Any such disclaimers, restrictions, disclosures or notices are deemed incorporated in the portion of any Website Information that you review or download.`,
  },
  {
    type: "p",
    text: `Despite the reasonable efforts of Beatrice Advisors to provide accurate information on the Website, Website Information may not be properly updated or corrected, even if Beatrice Advisors is aware that such information is inaccurate, outdated or otherwise inappropriate. Any dated Website Information is operative only as of the indicated date. To the extent any Website Information is contributed by a third party, Beatrice Advisors does not warrant the accuracy of any facts or information provided by, or endorse the opinions of, such third parties.`,
  },
  {
    type: "p",
    text: `You agree that you will not forward or distribute any of the Website Information to any other person or copy the Website Information in any manner whatsoever, in each case, without Beatrice Advisors’ prior written consent. Any distribution, forwarding or copying of the Website Information, in whole or in part, without Beatrice Advisors’ prior written consent is unauthorized and strictly prohibited.`,
  },
];

const DisclaimerPage = () => {
  useDocumentTitle("Disclaimer");
  const navigate = useNavigate();
  const location = useLocation();

  const setUserContext = useUserContextStore((state)=>state.setUserContext)

  const setUser = useAuthStore((state) => state.setUser);
  const setInputUserName = useAuthStore((state) => state.setInputUserName);
  const setShowMfaImage = useAuthStore((state) => state.setShowMfaImage);
  const requireMfa = useAuthStore((state) => state.requireMfa);
  const setLoading = useAuthStore((state) => state.setLoading);
  const setError = useAuthStore((state) => state.setError);
  const error = useAuthStore((state) => state.error);
  const setToken = useAuthStore((state)=> state.setToken);

  const [agreed, setAgreed] = useState(false);

  const state = location.state || {};
  const userId = useMemo(() => {
    const value = state.userId ?? state.userID ?? state.UserId ?? state.UserID;
    return value ? Number(value) : 0;
  }, [state]);
  const userName = useMemo(() => state.userName ?? state.username ?? "", [state]);
  const password = useMemo(() => state.password ?? "", [state]);

  const resolveNextAction = (data) => {
    const nextPageKeyRaw = data?.nextPageKey ?? data?.NextPageKey ?? "";
    const nextPageKey = nextPageKeyRaw.toString().toUpperCase();
    const getPostLoginRoute = (roleId, userId)  => (Number(roleId) === 1 ? "/AdminContact" :  `/Asset/Overview/${btoa(userId)}`);

    if (nextPageKey === "DISCLAIMER") {
      setError("Disclaimer is still required. Please try again.");
      return;
    }

    if (data?.status === "SUCCESS") {
      if (nextPageKey != "TOTP") {
          setToken(data.token);
          setError("");
          window.__USER_PROFILE_CONTEXT__ = {
            userProfileType: "ChangePassword",
            currentUserId: btoa(data.userId),
            userId: btoa(data.userId),
            contactId: btoa(data.userId),
            userName,
            roleId: data.roleId,
            tempPassword: "",
            passwordTemporary: false,
            isTempPasswordActive: false,
            postPasswordNextPageKey: nextPageKey,
          };
          setUserContext(window.__USER_PROFILE_CONTEXT__)
          navigate(getPostLoginRoute(data?.roleId, data?.userId));
          return;
        }
      const showMfaImage = data?.showMFAImage ?? data?.ShowMFAImage;
      const isMfaEnabled = data?.isMFAEnabled ?? data?.IsMFAEnabled;
      const userIdValue = data?.userId ?? data?.UserId;
      const roleIdValue = data?.roleId ?? data?.RoleId;
      const categoryIdValue = data?.categoryId ?? data?.CategoryId;

      setShowMfaImage(Boolean(showMfaImage));
      requireMfa(Boolean(isMfaEnabled));
      setInputUserName(userName);
      setUser({
        userid: userIdValue,
        roleid: roleIdValue,
        categoryid: categoryIdValue,
      });

      if (nextPageKey === "TOTP") {
        navigate(Boolean(isMfaEnabled) ? "/mfa-verification" : "/mfa-setup");
        return;
      }

      // Fallback to current auth UX: go to MFA setup/verification based on flag
      navigate(Boolean(isMfaEnabled) ? "/mfa-verification" : "/mfa-setup");
      return;
    }

    setError(data?.message || "Unable to continue. Please login again.");
    navigate("/");
  };

  const handleAccept = async () => {
    if (!agreed) {
      setError("Please agree to the Disclaimer.");
      return;
    }

    if (!userId || !userName || !password) {
      setError("Session expired. Please login again.");
      navigate("/");
      return;
    }

    try {
      setLoading(true);
      setError("");

      await acceptDisclaimer({
        ContactId: Number(userId),
        UserId: 0,
        IsAccepted: true,
        AcceptedFromIp: "",
      });

      const response = await login({ userName, password });
      resolveNextAction(response?.data || {});
    } catch {
      setError("Unable to save Disclaimer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-lg container-fluid-md">
      <article
        className="white_cbox"
        style={{ padding: 15, marginTop: 12, marginBottom: 12 }}
      >
        <div style={{ marginBottom: 6 }}>Beatrice Advisors LP</div>

        <div
          style={{
            maxHeight: 420,
            overflow: "auto",
            border: "1px solid #e6e6e6",
            borderRadius: 6,
            padding: 12,
            background: "#fff",
          }}
        >
          {DISCLAIMER_CONTENT.map((item, idx) => {
            if (item.type === "h4") return <h4 key={idx}>{item.text}</h4>;
            return <p key={idx}>{item.text}</p>;
          })}
        </div>

        {error && (
          <div style={{ marginTop: 10, color: "#c72c2c" }}>
            {error}
          </div>
        )}

        <div className="row" style={{ marginTop: 12 }}>
          <div className="col-sm-6" style={{ display: "flex", gap: 8 }}>
            <input
              type="checkbox"
              id="chkAgree"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <label htmlFor="chkAgree" style={{ margin: 0 }}>
              Agree
            </label>
          </div>
          <div className="col-sm-6">
            <button
              type="button"
              id="btnAgree"
              className="btn btn-primary"
              style={{ float: "right" }}
              onClick={handleAccept}
            >
              Save
            </button>
          </div>
        </div>
      </article>
    </div>
  );
};

export default DisclaimerPage;
